/**
 * The education entries' text — title and summary — one file per language,
 * `src/content/education/<language>.json`, keyed by slug. The rest of each entry is
 * in lib/educationContent.ts and is the same in every language.
 *
 * English is bundled; another language's file is fetched with the rest of that
 * language when it is chosen (`loadEducationText`, called by i18n.ts), and until
 * then the reader gets English.
 *
 * Kept apart from educationContent.ts so i18n.ts can load a language's file
 * without importing anything that imports i18n.
 */
import english from '../content/education/en.json';
import { isCompleteEducationText, type EducationText } from './languageGate';
import { DEFAULT_LANGUAGE, type Language } from './language';

export type { EducationText };

/** Every other language's file, fetched on demand. */
const LAZY_FILES = import.meta.glob<Record<string, EducationText>>(
  ['../content/education/*.json', '!../content/education/en.json'],
  { import: 'default' },
);

const loaded: Partial<Record<Language, Record<string, EducationText>>> = {
  [DEFAULT_LANGUAGE]: english as Record<string, EducationText>,
};

/** Fetch `lang`'s file, if it has one. Resolves once it is in (at once for English). */
export async function loadEducationText(lang: Language): Promise<void> {
  if (loaded[lang]) return;
  const load = LAZY_FILES[`../content/education/${lang}.json`];
  if (load) loaded[lang] = await load();
}

/** A language's file, if it has one and it is loaded. */
export function educationTextFor(lang: Language): Record<string, EducationText> | undefined {
  return loaded[lang];
}

/** True when `lang` is loaded with a title and a summary for every entry English has. */
export function hasEducationContent(lang: Language): boolean {
  return isCompleteEducationText(loaded[DEFAULT_LANGUAGE]!, educationTextFor(lang));
}
